import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';

// ── Ліміти FREE тарифу ──────────────────────────────────────────────────────
export const FREE_LIMITS = {
  maxProjects: 1,
  maxReports: 2,
  maxReportItems: 50,
  maxPreliminaryItems: 100,
  maxTemplates: 3,
  pdfPerMonth: 1,
  // Повністю заблоковані на FREE
  blockedFeatures: [
    'gantt',
    'ai_import',
    'ai_calculator',
    'client_link',
    'client_chat',
    'tenders',
    'excel_export',
    'branding',
    'google_drive',
    'directory_full',
    'partner_commission',
    'files',
    'lihtar_matrix',
    'custom_units',
  ],
};

// ── Повідомлення paywall ─────────────────────────────────────────────────────
export const PAYWALL_MESSAGES = {
  maxProjects: 'Ви досягли ліміту FREE — 1 об\'єкт. Перейдіть на PRO для необмеженої кількості об\'єктів.',
  maxReports: 'На FREE тарифі доступно до 2 звітів. Перейдіть на PRO для необмеженої кількості.',
  maxReportItems: 'На FREE тарифі до 50 позицій у звіті. Перейдіть на PRO для необмеженої кількості.',
  maxPreliminaryItems: 'На FREE тарифі до 100 позицій у попередньому кошторисі.',
  maxTemplates: 'На FREE тарифі до 3 шаблонів. Перейдіть на PRO для необмеженої кількості.',
  pdfPerMonth: 'Ви використали ліміт PDF на цей місяць. На PRO — необмежено і без вотермарку.',
  gantt: 'Графік робіт (Gantt) доступний на тарифі PRO.',
  ai_import: 'AI-імпорт доступний на тарифі PRO.',
  ai_calculator: 'AI-помічник калькулятора доступний на тарифі PRO.',
  client_link: 'Посилання для клієнта доступне на тарифі PRO.',
  client_chat: 'Чат з клієнтом доступний на тарифі PRO.',
  tenders: 'Тендери доступні на тарифі PRO.',
  excel_export: 'Excel-експорт доступний на тарифі PRO.',
  branding: 'Брендування доступне на тарифі PRO.',
  google_drive: 'Google Drive інтеграція доступна на тарифі PRO.',
  directory_full: 'Повний доступ до довідника (підказки, додавання) доступний на тарифі PRO.',
  partner_commission: 'Партнерські комісії доступні на тарифі PRO.',
  files: 'Завантаження файлів та документів доступне на тарифі PRO.',
  lihtar_matrix: 'LIHTAR Matrix — AI-розрахунок матеріалів — доступний на тарифі PRO.',
  custom_units: 'Власні одиниці виміру доступні на тарифі PRO.',
  default: 'Ця функція доступна на тарифі PRO.',
};

// ── Хук useSubscription ──────────────────────────────────────────────────────
export const useSubscription = (userId) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = useCallback(async () => {
    if (!userId) {
      setSubscription(null);
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error || !data) {
        // Немає підписки — створюємо FREE
        setSubscription({ plan: 'free', status: 'expired' });
      } else {
        // Перевіряємо чи тріал/підписка ще активна
        const now = new Date();
        const trialEnd = data.trial_ends_at ? new Date(data.trial_ends_at) : null;
        const periodEnd = data.current_period_ends_at ? new Date(data.current_period_ends_at) : null;

        let effectivePlan = data.plan;
        let effectiveStatus = data.status;

        if (data.status === 'trialing' && trialEnd && now > trialEnd) {
          // Тріал закінчився
          effectivePlan = 'free';
          effectiveStatus = 'expired';
          // Оновлюємо в базі
          await supabase
            .from('subscriptions')
            .update({ plan: 'free', status: 'expired', updated_at: new Date().toISOString() })
            .eq('id', data.id);
        } else if (data.status === 'active' && periodEnd && now > periodEnd) {
          // Підписка закінчилась
          effectivePlan = 'free';
          effectiveStatus = 'expired';
          await supabase
            .from('subscriptions')
            .update({ plan: 'free', status: 'expired', updated_at: new Date().toISOString() })
            .eq('id', data.id);
        }

        setSubscription({
          ...data,
          plan: effectivePlan,
          status: effectiveStatus,
        });
      }
    } catch (err) {
      console.error('Subscription fetch error:', err);
      setSubscription({ plan: 'free', status: 'expired' });
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Refetch при поверненні на вкладку — синхронізуємо стан з БД
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchSubscription();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchSubscription]);

  // Періодична синхронізація — раз на годину
  useEffect(() => {
    const id = setInterval(fetchSubscription, 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchSubscription]);

  // ── Перевірки (обчислюємо динамічно, з урахуванням поточного часу) ──
  // Це гарантує що тріал експайриться навіть якщо юзер не виходив з сесії
  const _now = new Date();
  const _trialEnd = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const _periodEnd = subscription?.current_period_ends_at ? new Date(subscription.current_period_ends_at) : null;

  const isTrial = subscription?.plan === 'pro'
    && subscription?.status === 'trialing'
    && _trialEnd && _now <= _trialEnd;

  const isActive = subscription?.plan === 'pro'
    && subscription?.status === 'active'
    && (!_periodEnd || _now <= _periodEnd);

  const isPro = isTrial || isActive;
  const trialUsed = subscription?.trial_used === true;

  const trialDaysLeft = (() => {
    if (!isTrial || !subscription?.trial_ends_at) return 0;
    const diff = new Date(subscription.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  // Перевірка чи фіча заблокована
  const isFeatureBlocked = (featureKey) => {
    if (isPro) return false;
    return FREE_LIMITS.blockedFeatures.includes(featureKey);
  };

  // Перевірка ліміту кількості
  const isLimitReached = (limitKey, currentCount) => {
    if (isPro) return false;
    const limit = FREE_LIMITS[limitKey];
    if (limit === undefined) return false;
    return currentCount >= limit;
  };

  // Отримати повідомлення для paywall
  const getPaywallMessage = (key) => {
    return PAYWALL_MESSAGES[key] || PAYWALL_MESSAGES.default;
  };

  return {
    subscription,
    loading,
    isPro,
    isTrial,
    isActive,
    trialUsed,
    trialDaysLeft,
    isFeatureBlocked,
    isLimitReached,
    getPaywallMessage,
    refetch: fetchSubscription,
  };
};
